import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Typography from "@/components/ui/typography";
import {
  getMentorLicensees,
  MentorLicenseesKeys,
} from "@/features/founders/api/mentor-founder";

const MentorFounders: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: [MentorLicenseesKeys.GET_LICENSEES],
    queryFn: () => getMentorLicensees(),
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Typography size="h2" className="text-[#1e1e24]">
              My Founders
            </Typography>
            <Typography size="b2" className="text-[#767677]">
              View and manage your founders.
            </Typography>
          </div>
        </div>

        <div className="bg-[#f9f9f9] rounded-2xl p-6">
          <Typography size="l2" className="text-[#767677] font-semibold">
            Founders
          </Typography>
          <div className="bg-[#fdfdfd] p-2 mt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            ) : !data?.items?.length ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Users className="h-5 w-5 text-[#767677]" />
                <Typography
                  size="l2"
                  className="mt-2 text-[#767677] font-semibold"
                >
                  No founders yet
                </Typography>
              </div>
            ) : (
              <Table bordered={false}>
                <TableHeader className="bg-transparent">
                  <TableRow>
                    <TableHead className="px-3 py-1.5">
                      <Typography
                        size="l4"
                        className="font-semibold tracking-[-0.4px] text-[#767677]"
                      >
                        Name
                      </Typography>
                    </TableHead>
                    <TableHead className="px-3 py-1.5">
                      <Typography
                        size="l4"
                        className="font-semibold tracking-[-0.4px] text-[#767677]"
                      >
                        Email
                      </Typography>
                    </TableHead>
                    <TableHead className="px-3 py-1.5">
                      <Typography
                        size="l4"
                        className="font-semibold tracking-[-0.4px] text-[#767677]"
                      >
                        Created Date
                      </Typography>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items?.map((f) => (
                    <TableRow
                      key={f._id}
                      className="hover:bg-sds-neutral-00 transition-colors"
                    >
                      <TableCell className="px-3 py-1.5">
                        <Typography
                          size="l3"
                          className="tracking-[-0.48px] text-[#494949] font-semibold"
                        >
                          {`${f.firstName} ${f.lastName}`}
                        </Typography>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Typography
                          size="l3"
                          className="tracking-[-0.48px] text-[#494949]"
                        >
                          {f.email}
                        </Typography>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Typography
                          size="l4"
                          className="tracking-[-0.4px] text-[#494949]"
                        >
                          {new Date(f.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MentorFounders;
